import type { components } from '@app/api/types';
export const APPROVAL_EVENTS = [
  'agent_creation',
  'capability_addition',
  'capability_removal',
] as const satisfies readonly components['schemas']['EnvironmentApprovalRule']['event'][];
export const APPROVAL_CLASSES = ['read', 'write', 'high', 'public', 'a2a'] as const;
export type ApprovalRule = components['schemas']['EnvironmentApprovalRule'];
export type EnvironmentApprovalRules = components['schemas']['EnvironmentApprovalRules'];
export type EnvironmentApprovalRulesInput = components['schemas']['EnvironmentApprovalRulesInput'];
export type ApprovalEvent = ApprovalRule['event'];
export type ApprovalClass = ApprovalRule['classes'][number];
export type ApprovalMode = ApprovalRule['mode'];

/** P28 — quem aplica a imagem no Deployment depois de a aprovação aplicar o contrato. */
export type DeploymentMode = EnvironmentApprovalRules['deployment_after_approval'];
export const DEPLOYMENT_MODES = ['manual', 'automatic'] as const satisfies readonly DeploymentMode[];

/**
 * O valor a desenhar enquanto a leitura não chegou, e para dado fora do vocabulário.
 *
 * `manual` é o comportamento de todo ambiente que já existia, e é o que a coluna dá por
 * omissão. Desenhar `automatic` por engano diria à pessoa que aquele ambiente sobe sozinho —
 * a afirmação mais cara de errar das duas.
 */
export function deploymentOrDefault(mode: DeploymentMode | undefined): DeploymentMode {
  return mode === 'automatic' ? 'automatic' : 'manual';
}

/** Keep the creation restriction in the draft as well as in the controls. */
export function withApprovalMode(rule: ApprovalRule, mode: ApprovalMode): ApprovalRule {
  if (mode === 'granular') {
    if (rule.event === 'agent_creation') return rule;
    return { event: rule.event, mode, classes: [...rule.classes] };
  }
  return { event: rule.event, mode, classes: [] };
}

/**
 * As classes que cada evento tinha **ao sair** de `granular`, para o rascunho as
 * repor quando ele voltar (§8.4, aprovado pelo mrolim em 12/09).
 */
export type ClassMemory = Partial<Record<ApprovalEvent, ApprovalClass[]>>;

/**
 * Troca o modo de UMA linha, lembrando as classes.
 *
 * # POR QUE A MEMÓRIA VIVE AQUI E NÃO DENTRO DE `withApprovalMode`
 *
 * `withApprovalMode` continua a zerar as classes em `sempre`/`nunca`, e tem de
 * continuar: é o §5.1a («`always` e `never` têm classes vazias»), e o que o `PUT`
 * manda sai dali. O que faltava não era a limpeza — era alguém guardar o que foi
 * limpo. Manter as duas responsabilidades separadas deixa a função do contrato
 * pura e testável sozinha, e põe a memória onde ela é: um estado do RASCUNHO.
 *
 * # O QUE ISTO NÃO PROMETE
 *
 * A memória é do rascunho, portanto **morre com a sessão**. Quem sair da página e
 * voltar encontra as caixas como estão gravadas — vazias, se era isso que estava
 * no banco. É por isso que o aviso da §8.5 não é acessório desta correção: ele é a
 * outra metade, e cobre o `granular` vazio que vem da migração (§4.6) e o que
 * sobra de desmarcar a última caixa.
 */
export function applyApprovalMode(
  rules: readonly ApprovalRule[],
  index: number,
  mode: ApprovalMode,
  memory: ClassMemory,
): { rules: ApprovalRule[]; memory: ClassMemory } {
  const current = rules[index];
  if (!current) return { rules: [...rules], memory };

  // Sair de `granular` é o único momento em que há algo para guardar — e guarda-se
  // mesmo quando a lista está vazia, senão «desmarquei tudo e fui a Sempre» voltaria
  // com as caixas antigas, inventando uma escolha que a pessoa desfez.
  const next: ClassMemory =
    current.mode === 'granular' && mode !== 'granular'
      ? { ...memory, [current.event]: [...current.classes] }
      : memory;

  const trocada = withApprovalMode(current, mode);
  const lembradas = next[current.event];
  const reposta =
    trocada.mode === 'granular' && lembradas
      ? { ...trocada, classes: [...lembradas] }
      : trocada;

  return { rules: rules.map((rule, i) => (i === index ? reposta : rule)), memory: next };
}

/** Missing persisted rows are restrictive, including newly created environments. */
export function effectiveRules(rules: readonly ApprovalRule[]): ApprovalRule[] {
  return APPROVAL_EVENTS.map((event) => {
    const rule = rules.find((candidate) => candidate.event === event);
    return rule ? { ...rule, classes: [...rule.classes] } : { event, mode: 'always', classes: [] };
  });
}

export function approvalSummary(
  rules: readonly ApprovalRule[],
): 'human' | 'automatic' | 'conditional' {
  const effective = effectiveRules(rules);
  if (effective.every((rule) => rule.mode === 'always')) return 'human';
  if (effective.every((rule) => rule.mode === 'never')) return 'automatic';
  return 'conditional';
}

export function workflowLink(environmentId: string): string {
  return `/approval-workflow?environment=${encodeURIComponent(environmentId)}`;
}
